/**
 * HandoffBanner — Story 12.3 / Epic E12.
 *
 * Renderiza um aviso amarelo destacado quando a conversa está em
 * `human_handoff` — sinaliza ao operador (e a colegas que abram a página)
 * que a Rafa está pausada e há intervenção humana em andamento.
 *
 * Lê o snapshot de quem assumiu via `conversation.metadata.handoff_by_user_name`
 * (gravado pelo backend em /handoff — Story 12.2). Se ausente (race com
 * dados antigos), mostra a forma genérica sem nome.
 */

import { UserCheck } from 'lucide-react';

import { formatRelativeFromNow } from '@/lib/format-date';

interface HandoffMetadata {
  handoff_by_user_id?: string;
  handoff_by_user_name?: string;
  handoff_at?: string;
}

export interface HandoffBannerProps {
  /** Metadata raw da conversation. Tolerante a null/undefined. */
  metadata: unknown;
  /** Para testes determinísticos. Default = new Date(). */
  now?: Date;
}

function parseMetadata(raw: unknown): HandoffMetadata {
  if (raw === null || typeof raw !== 'object') return {};
  const m = raw as Record<string, unknown>;
  const out: HandoffMetadata = {};
  if (typeof m.handoff_by_user_id === 'string') out.handoff_by_user_id = m.handoff_by_user_id;
  if (typeof m.handoff_by_user_name === 'string') out.handoff_by_user_name = m.handoff_by_user_name;
  if (typeof m.handoff_at === 'string') out.handoff_at = m.handoff_at;
  return out;
}

export function HandoffBanner({ metadata, now }: HandoffBannerProps) {
  const m = parseMetadata(metadata);
  const since = m.handoff_at ? formatRelativeFromNow(m.handoff_at, now) : null;
  const who = m.handoff_by_user_name ?? null;

  let label: string;
  if (who && since) {
    label = `Conversa assumida por ${who} ${since}.`;
  } else if (who) {
    label = `Conversa assumida por ${who}.`;
  } else {
    label = 'Conversa assumida por humano.';
  }

  return (
    <div
      data-testid="handoff-banner"
      role="status"
      aria-live="polite"
      className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200"
    >
      <UserCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-0.5 text-sm">
        <p className="font-medium">{label}</p>
        <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
          A Rafa está pausada nessa conversa.
        </p>
      </div>
    </div>
  );
}

export default HandoffBanner;
